import React, { useMemo, useState } from "react";
import { Box, Typography, Fab, Chip, Button, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import { EditableDataGridStyle, AppGridColDef } from "../../shared/EditableDataGridStyle";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { formatIsoDate, formatCurrency } from "../../utils/namesFormater";
import { currencyColumn, enumColumn, handlerCodeColumn, notesEditableColumn, editableCurrencyColumn } from "../../shared/UIParts/columnsFormatter";
import { useContactNameColumn } from "../../hooks/useContactNameColumn";
import { POLICY_COLORS } from "../../utils/insuranceUtil";

import {
  formatMainInsuranceBranch,
  formatInsuranceProductType,
  formatPremiumType,
  formatPlanClassification,
  formatHealthCoverageNormalizedCategory,
  subBranchFormatters,
} from "../../utils/insuranceUtil";
import { formatProductType } from "../../utils/enumFormatter";

import { useInsuranceDataByContactIds, usePartialUpdateInsuranceData } from "../../hooks/insurance_data/useInsuranceData";
import { useLatestManualMimshaksByContacts } from "../../hooks/useManualMimshaksData";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { InsuranceData } from "../../interfaces/DTO/insurance_data/InsuranceData";
import {
  InsuranceProductType,
  MainInsuranceBranch,
  PlanClassification,
  PremiumType,
  LifeInsuranceSubCategory,
  DisabilityInsuranceSubCategory,
  HealthCoverageNormalizedCategory,
} from "../../interfaces/DTO/insurance_data/InsuranceData";
import InsuranceUpload from "./InsuranceUpload";
import HealthCoverageAnalysisDialog from "./HealthCoverageAnalysisDialog";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import InsuranceProductDrawer from "../PersonFinancialData/Insurance/InsuranceProductDrawer";
import InsuranceBranchMetrics from "./InsuranceBranchMetrics";
import { InsuranceType } from "../../interfaces/DTO/mislaka_xml_insurence_models";

interface Props {
  contacts: Contact[];
  insuranceMimshakData?: MimshakData[];
  reportedPeriod: string;
}

const InsuranceDataTableForContacts: React.FC<Props> = ({ contacts, insuranceMimshakData = [], reportedPeriod }) => {
  const contactIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
  const { data = [], isLoading: insuranceDataLoading } = useInsuranceDataByContactIds(contactIds);
  const { mutateAsync: updateInsuranceData } = usePartialUpdateInsuranceData();
  const contactNameColumn = useContactNameColumn();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedReportedPeriod, setSelectedReportedPeriod] = useState<string | null>(null);
  const [analysisContact, setAnalysisContact] = useState<Contact | null>(null);
  const [contactMenuAnchor, setContactMenuAnchor] = useState<HTMLElement | null>(null);
  const { data: manualMimshakData = [] } = useLatestManualMimshaksByContacts(contactIds);

  // טבלת מיפוי בין ענף משני לסוג כיסוי (insurance_kind)
  const SUB_BRANCH_TO_INSURANCE_KIND: Record<string, InsuranceType> = {
    // ענפי ביטוח חיים
    [LifeInsuranceSubCategory.DISABILITY]: InsuranceType.ACCIDENTAL_DISABILITY, // נכות מתאונה
    [LifeInsuranceSubCategory.DEATH_ONLY]: InsuranceType.DEATH, // מוות
    [LifeInsuranceSubCategory.ACCIDENTAL_DEATH]: InsuranceType.ACCIDENTAL_DEATH, // מוות בתאונה

    // ענפי ביטוח נכות
    [DisabilityInsuranceSubCategory.DISABILITY_COMPENSATION]: InsuranceType.LOSS_OF_WORK_CAPACITY, // אובדן כושר עבודה
    [DisabilityInsuranceSubCategory.DISABILITY_WAIVER]: InsuranceType.LOSS_OF_WORK_CAPACITY, // אובדן כושר עבודה
  };

  // מיפוי מספר פוליסה לצבע
  const policyColorMap = useMemo(() => {
    const map = new Map<string, string>();
    const uniquePolicies = Array.from(new Set(data.map((d) => d.policy_number)));
    uniquePolicies.forEach((policyNumber, index) => {
      map.set(policyNumber, POLICY_COLORS[index % POLICY_COLORS.length]);
    });
    return map;
  }, [data]);

  // חילוץ תקופות דיווח ייחודיות - תקופות שהפער ביניהן עד 30 יום מאוחדות תחת החדשה ביותר
  const uniqueReportedPeriods = useMemo(() => {
    const periods = Array.from(new Set(data.map((d) => d.reported_period).filter(Boolean)));
    const sorted = periods.sort((a, b) => b.localeCompare(a));

    const groups: string[] = [];
    for (const period of sorted) {
      if (groups.length === 0) {
        groups.push(period);
      } else {
        const lastDate = new Date(groups[groups.length - 1]);
        const currentDate = new Date(period);
        const diffDays = (lastDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > 30) {
          groups.push(period);
        }
      }
    }
    return groups;
  }, [data]);

  // בודק אם תקופת דיווח של שורה שייכת לקבוצה של תקופה נבחרת (פער עד 30 יום)
  const isInPeriodGroup = (rowPeriod: string, groupPeriod: string): boolean => {
    const groupDate = new Date(groupPeriod);
    const rowDate = new Date(rowPeriod);
    const diffDays = (groupDate.getTime() - rowDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 30;
  };

  // בחירת תקופה אוטומטית - התקופה הראשונה (החדשה ביותר)
  React.useEffect(() => {
    if (uniqueReportedPeriods.length > 1 && !selectedReportedPeriod) {
      setSelectedReportedPeriod(uniqueReportedPeriods[0]);
    }
  }, [uniqueReportedPeriods, selectedReportedPeriod]);

  // נתונים לפני סינון ענף - למטריקות (מסוננים לפי תקופה אם יש יותר מאחת)
  const allContactData = useMemo(() => {
    if (uniqueReportedPeriods.length > 1 && selectedReportedPeriod) {
      return data.filter((d) => d.reported_period && isInPeriodGroup(d.reported_period, selectedReportedPeriod));
    }
    return data;
  }, [data, uniqueReportedPeriods.length, selectedReportedPeriod]);

  // פונקציה לחיפוש מוצר ב-insuranceMimshakData לפי handler_code, policy_number ו-contact_id
  const findProductInMimshakData = (
    handlerCode: string,
    policyNumber: string,
    contactId: number
  ): Product | null => {
    if (!insuranceMimshakData || insuranceMimshakData.length === 0) {
      return null;
    }

    // חיפוש ב-mimshak data
    for (const mimshakData of insuranceMimshakData) {
      // בדיקה שה-mimshak שייך ל-contact הנכון
      if (mimshakData.contact_id !== contactId) {
        continue;
      }

      // בדיקה שיש data ומוצרים
      if (!mimshakData.data?.products) {
        continue;
      }

      // חיפוש המוצר המתאים - בודקים גם את handler_code וגם שיש פוליסה עם policy_number
      const product = mimshakData.data.products.find(
        (p: Product) =>
          p.details?.handler_code === handlerCode &&
          p.policies_or_accounts?.some((policy) => policy.policy_number === policyNumber)
      );

      if (product) {
        return product;
      }
    }

    return null;
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];

    // פילטר לפי תקופת דיווח אם יש יותר מתקופה אחת
    if (uniqueReportedPeriods.length > 1 && selectedReportedPeriod) {
      filtered = filtered.filter((d) => d.reported_period && isInPeriodGroup(d.reported_period, selectedReportedPeriod));
    }

    // סינון לפי ענף נבחר
    if (selectedBranch) {
      if (selectedBranch === "LIFE") {
        // חיים - רק חיים ואובדן כושר עבודה (לא כולל חיסכון טהור)
        filtered = filtered.filter(
          (d) =>
            (d.main_branch === MainInsuranceBranch.LIFE && d.sub_branch !== LifeInsuranceSubCategory.PURE_SAVINGS) ||
            d.main_branch === MainInsuranceBranch.LOSS_OF_WORK_CAPACITY
        );
      } else if (selectedBranch === "SAVINGS") {
        // חיסכון - רק פוליסות חיסכון טהור
        filtered = filtered.filter(
          (d) =>
            d.main_branch === MainInsuranceBranch.LIFE && d.sub_branch === LifeInsuranceSubCategory.PURE_SAVINGS
        );
      } else if (selectedBranch === "HEALTH_AND_OTHERS") {
        // בריאות - כל השאר (לא רכב, דירה, חיים, אובדן כושר עבודה, סיעוד וחיסכון)
        filtered = filtered.filter(
          (d) =>
            d.main_branch !== MainInsuranceBranch.CAR &&
            d.main_branch !== MainInsuranceBranch.HOME &&
            d.main_branch !== MainInsuranceBranch.LIFE &&
            d.main_branch !== MainInsuranceBranch.LOSS_OF_WORK_CAPACITY &&
            d.main_branch !== MainInsuranceBranch.LONG_TERM_CARE &&
            !(d.main_branch === MainInsuranceBranch.LIFE && d.sub_branch === LifeInsuranceSubCategory.PURE_SAVINGS)
        );
      } else {
        // ענף ספציפי (רכב, דירה, או סיעוד)
        filtered = filtered.filter((d) => d.main_branch === selectedBranch);
      }
    }

    // הוספת product לכל שורה אם קיים, וחישוב סכום כיסוי ושם כיסוי
    return filtered.map((row) => {
      const product = findProductInMimshakData(
        row.handler_code,
        row.policy_number,
        row.contact_id
      );

      // חישוב סכום כיסוי ושם כיסוי
      let calculatedCoverageSum = row.coverage_sum;
      let coverageNameFromProduct: string | null = null;

      // אם אין ערך ידני בשדה coverage_sum, נחפש בפוליסה
      if (calculatedCoverageSum == null && product?.policies_or_accounts) {
        const subBranch = row.sub_branch;

        // חיפוש סוג כיסוי מתאים בטבלת המיפוי
        const targetInsuranceKind = SUB_BRANCH_TO_INSURANCE_KIND[subBranch];

        // חיפוש הכיסוי המתאים בפוליסה המתאימה
        if (targetInsuranceKind !== undefined) {
          // חיפוש הפוליסה המתאימה לפי policy_number
          const matchingPolicy = product.policies_or_accounts.find(
            (policy) => policy.policy_number === row.policy_number
          );

          if (matchingPolicy?.insurence_coverages) {
            for (const coverage of matchingPolicy.insurence_coverages) {
              if (coverage.coverage_details?.insurance_kind === targetInsuranceKind) {
                const insuranceSum = coverage.coverage_details?.insurance_sum;
                if (insuranceSum != null && insuranceSum > 0) {
                  calculatedCoverageSum = insuranceSum;
                  coverageNameFromProduct = coverage.coverage_name || null;
                  break;
                }
              }
            }
          }
        }
      }

      // הוספת שם כיסוי להערות אם נמצא
      let enhancedNotes = row.notes;
      if (coverageNameFromProduct) {
        enhancedNotes = enhancedNotes
          ? `${enhancedNotes} | ${coverageNameFromProduct}`
          : coverageNameFromProduct;
      }

      return {
        ...row,
        product,
        // אם אין ערך ב-coverage_sum, נשתמש ב-calculatedCoverageSum כברירת מחדל
        coverage_sum: row.coverage_sum ?? calculatedCoverageSum,
        // אם אין ערך ב-notes, נשתמש ב-enhancedNotes כברירת מחדל
        notes: row.notes || enhancedNotes,
        // שמירת הערכים המחושבים גם כן לצורך הצגה
        calculatedCoverageSum,
        enhancedNotes,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, insuranceMimshakData, selectedBranch, uniqueReportedPeriods.length, selectedReportedPeriod]);

  const insuranceDataReportedPeriod = useMemo(() => {
    if (filteredData.length > 0 && filteredData[0].reported_period) {
      return formatIsoDate(filteredData[0].reported_period);
    }
    return null;
  }, [filteredData]);

  const formatSubBranch = (main: string, sub: string): string => {
    const formatter = subBranchFormatters[main];
    return formatter ? formatter(sub) : sub || "-";
  };

  const uniquePolicyCount = useMemo(() => new Set(filteredData.map((d) => d.policy_number)).size, [filteredData]);
  const policyNumberWidth = uniquePolicyCount > 3 ? 120 : 160;

  const columns: AppGridColDef[] = [
    // הצגת עמודת לקוח רק אם יש יותר מאיש קשר אחד
    ...(contacts.length > 1 ? [contactNameColumn] : []),
    enumColumn("main_branch", "ענף ראשי", Object.values(MainInsuranceBranch), formatMainInsuranceBranch, 150),
    enumColumn("normalized_category", "קטגוריה", Object.values(HealthCoverageNormalizedCategory), formatHealthCoverageNormalizedCategory, 150),
    {
      field: "policy_number",
      headerName: "מס׳ פוליסה",
      width: policyNumberWidth,
      renderCell: (params) => {
        const hasProduct = !!params.row.product;
        const policyColor = policyColorMap.get(params.value) || "#999999";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: policyColor,
                flexShrink: 0,
              }}
            />
            {hasProduct ? (
              <span
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {params.value}
              </span>
            ) : (
              <span>{params.value}</span>
            )}
          </Box>
        );
      },
    },
    {
      field: "sub_branch",
      headerName: "ענף משני",
      width: 150,
      type: "singleSelect",
      valueGetter: (params, row) => {
        // מחזיר את הטקסט המעוצב לצורך SORT ו-EXPORT
        return formatSubBranch(row.main_branch, params);
      },
      renderCell: (params) => params.value,
    },
    {
      field: "insurance_product_type",
      headerName: "סוג מוצר",
      width: 150,
      type: "singleSelect",
      valueGetter: (params, row) => {
        // אם יש product מ-mimshak, השתמש בסוג המוצר שלו
        if (row.product?.details?.product_type != null) {
          // מחזיר את הטקסט המעוצב לצורך SORT ו-EXPORT
          return formatProductType(row.product.details.product_type);
        }
        // אחרת, החזר את הטקסט המעוצב של insurance_product_type
        if (typeof params === 'string') {
          return formatInsuranceProductType(params as InsuranceProductType);
        }
        return "-";
      },
      renderCell: (params) => params.value,
    },
    handlerCodeColumn(),
    currencyColumn("premium_amount", "עלות", 110, true),
    editableCurrencyColumn("coverage_sum", "סכום כיסוי", 120),
    notesEditableColumn("notes", "הערות"),
    enumColumn("premium_type", "סוג עלות", Object.values(PremiumType), formatPremiumType, 110),
    enumColumn("plan_classification", "סיווג תכנית", Object.values(PlanClassification), formatPlanClassification, 110),
    {
      field: "reported_period",
      headerName: "תקופה מדווחת",
      width: 140,
      renderCell: (params) => formatIsoDate(params.value),
    },
    {
      field: "insurance_start_date",
      headerName: "תאריך התחלה",
      width: 140,
      renderCell: (params) => formatIsoDate(params.value),
    },
    {
      field: "insurance_end_date",
      headerName: "תאריך סיום",
      width: 140,
      renderCell: (params) => formatIsoDate(params.value),
    },
  ];

  const handleUploadComplete = () => {
    setShowUpload(false);
  };

  // טיפול בלחיצה על שורה
  const handleRowClick = (params: any) => {
    const product = params.row.product;

    if (product) {
      setSelectedProduct(product);
    }
    // אם לא נמצא מוצר, לא יקרה כלום
  };

  const handleCloseDrawer = () => {
    setSelectedProduct(null);
  };

  // עדכון חלקי של נתוני ביטוח
  const handlePartialUpdate = async (id: number | string, fields: Partial<InsuranceData>) => {
    await updateInsuranceData({ id: Number(id), fields });
  };

  // סינון נתונים מבוסס בחירה - אם לא נבחרו שורות, נשתמש בכל הנתונים
  const selectedData = useMemo(() => {
    if (selectedRows.length === 0) {
      return filteredData;
    }
    return filteredData.filter((row) => selectedRows.includes(row.id));
  }, [filteredData, selectedRows]);

  // חישוב סיכומים
  const totals = useMemo(() => {
    return selectedData.reduce(
      (acc, row) => ({
        premium_amount: acc.premium_amount + (row.premium_amount || 0),
      }),
      { premium_amount: 0 }
    );
  }, [selectedData]);

  // אם אין נתונים ולא בטעינה - הצג את InsuranceUpload
  if (!insuranceDataLoading && filteredData.length === 0) {
    return (
      <Box sx={{ width: "100%", direction: "rtl" }}>
        <Typography variant="h6" textAlign="right" mb={2}>
          פוליסות ביטוח הר הביטוח
        </Typography>
        <InsuranceUpload onUploadComplete={handleUploadComplete} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", direction: "rtl" }}>
      {/* כותרת עם כפתור + */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6" textAlign="right">
          פוליסות ביטוח הר הביטוח
          {insuranceDataReportedPeriod && ` - לתקופה: ${insuranceDataReportedPeriod}`}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Fab
            color="primary"
            size="large"
            onClick={() => setShowUpload(!showUpload)}
            sx={{ width: 70, height: 70 }}
          >
            <AddIcon sx={{ fontSize: 40 }} />
          </Fab>
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            עדכון חדש
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Fab
            color="secondary"
            size="large"
            onClick={(e) => {
              if (contacts.length === 1) {
                setAnalysisContact(contacts[0]);
              } else {
                setContactMenuAnchor(e.currentTarget);
              }
            }}
            sx={{ width: 70, height: 70 }}
          >
            <HealthAndSafetyIcon sx={{ fontSize: 36 }} />
          </Fab>
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            ניתוח בריאות
          </Typography>
        </Box>

        <Menu
          anchorEl={contactMenuAnchor}
          open={Boolean(contactMenuAnchor)}
          onClose={() => setContactMenuAnchor(null)}
        >
          {contacts.map((c) => (
            <MenuItem
              key={c.id}
              onClick={() => {
                setAnalysisContact(c);
                setContactMenuAnchor(null);
              }}
            >
              {c.first_name} {c.last_name}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Chips לתקופות דיווח - מוצג רק אם יש יותר מתקופה אחת */}
      {uniqueReportedPeriods.length > 1 && (
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", ml: 1 }}>
            תקופות דיווח:
          </Typography>
          {uniqueReportedPeriods.map((period) => (
            <Chip
              key={period}
              label={formatIsoDate(period)}
              onClick={() => setSelectedReportedPeriod(period)}
              color={selectedReportedPeriod === period ? "primary" : "default"}
              variant={selectedReportedPeriod === period ? "filled" : "outlined"}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
      )}

      {/* אזור העלאה */}
      {showUpload && (
        <Box sx={{ mb: 3 }}>
          <InsuranceUpload onUploadComplete={handleUploadComplete} />
        </Box>
      )}

      {/* מטריקות לפי ענף */}
      <InsuranceBranchMetrics
        data={allContactData}
        selectedBranch={selectedBranch}
        onBranchClick={setSelectedBranch}
        insuranceMimshakData={insuranceMimshakData}
        manualMimshakData={manualMimshakData}
        contacts={contacts}
        reportedPeriod={reportedPeriod}
      />

      {/* טבלת נתונים */}
      <EditableDataGridStyle
        initialRows={filteredData}
        columns={columns}
        onPartialUpdate={handlePartialUpdate}
        loading={insuranceDataLoading}
        maxHeight="700px"
        onRowClick={handleRowClick}
        onRowSelectionModelChange={setSelectedRows}
        hideDeleteButton={true}
      />

      {/* שורת סיכום */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          px: 2,
        }}
      >
        {insuranceDataReportedPeriod && (
          <Box>
            לתקופת דיווח: <strong>{insuranceDataReportedPeriod}</strong>
          </Box>
        )}
        {selectedRows.length > 0 && (
          <Box>
            שורות נבחרות: <strong>{selectedData.length}</strong>
          </Box>
        )}
        {totals.premium_amount ? (
          <Box>
            סה"כ פרמיה:{" "}
            <strong>
              <span dir="ltr">{formatCurrency(totals.premium_amount)}</span>
            </strong>
          </Box>
        ) : null}
      </Box>

      {/* מגירת מוצר */}
      {selectedProduct && (
        <InsuranceProductDrawer product={selectedProduct} onClose={handleCloseDrawer} />
      )}

      {/* דיאלוג ניתוח כיסויי בריאות */}
      {analysisContact && (
        <HealthCoverageAnalysisDialog
          open={true}
          onClose={() => setAnalysisContact(null)}
          contact={analysisContact}
          reportedPeriod={insuranceDataReportedPeriod ?? reportedPeriod}
        />
      )}
    </Box>
  );
};

export default InsuranceDataTableForContacts;
